import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { addBOMItems } from "../../store/slices/bomSlice";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchDropdownData } from "../../store/slices/storeInwardSlice";
import toast from "react-hot-toast";
import Button, { ButtonType } from "../../components/Assets/Button";
import Select from "react-select";
import { UNITS } from "../../models/warehouseRawMaterial.model";
import {
  getUnitsByType,
  getDefaultUnitForType,
} from "../../utils/warehouse.util";

interface AddItemsToBOMProps {
  bomId?: string;
  onSuccess: (items: any[]) => void;
  checkBom?: boolean;
}

const AddItemsComponent: React.FC<AddItemsToBOMProps> = ({
  bomId,
  onSuccess,
  checkBom = true,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { products } = useSelector((state: RootState) => state.product);
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);

  const { selectedBOM: bom } = useSelector(
    (state: RootState) => state.billOfMaterials,
  );

  const [items, setItems] = useState<any[]>([]);
  const [productPick, setProductPick] = useState({ id: "", quantity: 1 });
  const [materialPick, setMaterialPick] = useState({
    id: "",
    quantity: 1,
    unit: "",
  });

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchDropdownData());
  }, [dispatch]);

  if (checkBom && !bom) {
    return <div className="p-4 text-sm text-gray-500">BOM not found</div>;
  }

  const addProduct = () => {
    if (!productPick.id) return toast.error("Select a product");
    const prod = products.find((p) => p._id === productPick.id);
    if (!prod) return;

    if (bom) {
      const existsInBOM = bom.items.some(
        (i) => i.type === "product" && i.product?._id === prod._id,
      );

      if (existsInBOM) {
        return toast.error("Product already exists in this BOM");
      }
    }

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.type === "product" && item.product === prod._id,
      );

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity:
                  Number(item.quantity || 0) + Number(productPick.quantity),
              }
            : item,
        );
      }

      return [
        ...prev,
        {
          type: "product",
          product: prod._id,
          name: prod.productName,
          quantity: productPick.quantity,
          gst: prod.gst,
          hsn: prod.hsn,
          price: 0,
        },
      ];
    });
    setProductPick({ id: "", quantity: 1 });
  };

  const addMaterial = () => {
    if (!materialPick.id) return toast.error("Select a material");
    if (!materialPick.unit) return toast.error("Select a unit");

    const mat = rawMaterials.find((m) => m._id === materialPick.id);
    if (!mat) return;

    if (bom) {
      const existsInBOM = bom.items.some(
        (i) => i.type === "material" && i.rawMaterial?._id === mat._id,
      );

      if (existsInBOM) {
        return toast.error("Material already exists in this BOM");
      }
    }

    setItems((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.type === "material" && item.rawMaterial === mat._id,
      );

      if (existingIndex >= 0) {
        return prev.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity:
                  Number(item.quantity || 0) + Number(materialPick.quantity),
                unit: item.unit || materialPick.unit,
              }
            : item,
        );
      }

      return [
        ...prev,
        {
          type: "material",
          rawMaterial: mat._id,
          product: mat._id,
          name: mat.name,
          quantity: materialPick.quantity,
          unit: materialPick.unit,
          gst: mat.gst,
          hsn: mat.hsn,
          price: 0,
        },
      ];
    });
    setMaterialPick({ id: "", quantity: 1, unit: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Add at least one item");

    try {
      if (checkBom && bomId) {
        await dispatch(addBOMItems({ bomId, items })).unwrap();
      }
      onSuccess(items);
    } catch (error) {
      console.log(error);
      toast.error("Failed to add items");
    }
  };

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "38px",
      fontSize: "0.75rem",
      borderRadius: "0.5rem",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100">
          <label className="block text-[10px] font-bold text-blue-800 mb-2 uppercase tracking-widest">
            Add Products
          </label>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Select
                options={products.map((p) => ({
                  value: p._id,
                  label: p.productName,
                }))}
                onChange={(opt: any) =>
                  setProductPick((p) => ({ ...p, id: opt?.value || "" }))
                }
                value={
                  products.find((p) => p._id === productPick.id)
                    ? {
                        value: productPick.id,
                        label: products.find((p) => p._id === productPick.id)
                          ?.productName,
                      }
                    : null
                }
                styles={selectStyles}
                menuPortalTarget={document.body}
                placeholder="Select Product"
              />
            </div>
            <div className="w-20">
              <input
                type="number"
                min={1}
                value={productPick.quantity}
                onChange={(e) =>
                  setProductPick((p) => ({ ...p, quantity: +e.target.value }))
                }
                className="w-full text-xs px-3 py-2 border rounded-lg h-[38px] outline-none"
              />
            </div>
            <Button
              type={ButtonType.Submit}
              onClick={addProduct}
              className="text-xs h-[38px] px-4"
            >
              Add
            </Button>
          </div>
        </div>

        <div className="p-4 bg-green-50/50 rounded-xl border border-green-100">
          <label className="block text-[10px] font-bold text-green-800 mb-2 uppercase tracking-widest">
            Add Materials
          </label>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <Select
                options={rawMaterials.map((rm) => ({
                  value: rm._id,
                  label: rm.name,
                  unitType: rm.unitType,
                }))}
                onChange={(opt: any) => {
                  setMaterialPick({
                    id: opt?.value || "",
                    quantity: 1,
                    unit: opt ? getDefaultUnitForType(opt.unitType) : "",
                  });
                }}
                value={
                  rawMaterials.find((m) => m._id === materialPick.id)
                    ? {
                        value: materialPick.id,
                        label: rawMaterials.find(
                          (m) => m._id === materialPick.id,
                        )?.name,
                      }
                    : null
                }
                styles={selectStyles}
                menuPortalTarget={document.body}
                placeholder="Select Material"
              />
            </div>
            <div className="w-20">
              <input
                type="number"
                min={1}
                value={materialPick.quantity}
                onChange={(e) =>
                  setMaterialPick((p) => ({ ...p, quantity: +e.target.value }))
                }
                className="w-full text-xs px-3 py-2 border rounded-lg h-[38px] outline-none"
              />
            </div>
            <div className="w-28">
              <Select
                options={
                  materialPick.id
                    ? getUnitsByType(
                        rawMaterials.find((m) => m._id === materialPick.id)
                          ?.unitType || "",
                      )
                    : UNITS
                }
                value={UNITS.find((u) => u.value === materialPick.unit) || null}
                onChange={(opt: any) =>
                  setMaterialPick((p) => ({ ...p, unit: opt?.value || "" }))
                }
                styles={selectStyles}
                menuPortalTarget={document.body}
                placeholder="Unit"
              />
            </div>
            <Button
              type={ButtonType.Submit}
              onClick={addMaterial}
              className="text-xs h-[38px] px-4"
            >
              Add
            </Button>
          </div>
        </div>

        {items.length > 0 && (
          <div className="border rounded-xl overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b text-[10px] font-bold text-gray-400 uppercase">
              New Items to Add
            </div>
            <ul className="divide-y divide-gray-100 max-h-[200px] overflow-y-auto bg-white">
              {items.map((item, idx) => (
                <li
                  key={idx}
                  className="flex justify-between items-center p-3 text-xs hover:bg-gray-50"
                >
                  <div className="flex flex-col">
                    <span className="font-bold text-gray-900">{item.name}</span>
                    <span className="text-[9px] uppercase font-semibold text-gray-400">
                      {item.type}
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold">
                      {item.quantity} {item.unit || "Units"}
                    </span>
                    <button
                      onClick={() =>
                        setItems((prev) => prev.filter((_, i) => i !== idx))
                      }
                      className="text-red-400 hover:text-red-600"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type={ButtonType.Cancel} onClick={() => onSuccess([])}>
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleSubmit}
            disabled={items.length === 0}
            className="px-8 bg-navyBlue-primary text-white"
          >
            Save Items
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddItemsComponent;
